/**
 * The Leaves page's tabs, and who sees which.
 *
 * ── Why tabs at all ────────────────────────────────────────────────────────
 *
 * Drops 1 to 5 shipped one scrolling page: the file-a-request form, the
 * approval queue, the calendar and your own leave, stacked. Nothing was
 * hidden, but nothing was where anybody reached for it either. Kyu opened it
 * on 7 September and said "I don't see All Requests and Approvals Tab here" —
 * Approvals was there, three screens down.
 *
 * The Apps Script has four tabs and the team already reads them in this order,
 * so this is not a design choice so much as a correction. It also falls under
 * the 4 September standing rule — "just dont change the process, steps and
 * flow" — which the single-page layout had quietly broken.
 *
 * ── Why a member sees two and an approver sees four ────────────────────────
 *
 * Kyu, 7 September: All Requests is for approvers only, him and Alex. The
 * approval queue was always approver-only.
 *
 * A tab a person cannot use is not shown to them at all rather than shown
 * disabled. A greyed tab is an invitation to ask why, and the answer — "that
 * one is for Kyu and Alex" — is not information anybody needed at that moment.
 *
 * Hiding a tab is NOT the access control. That lives in the database:
 * `leave_all_requests` refuses a non-approver outright, and the read policy
 * narrows the rows themselves. See 20270118000100. This file only decides what
 * is worth drawing.
 */

export type LeaveTab = 'calendar' | 'request' | 'all' | 'approvals';

/**
 * Order matters: the strip is rendered from these keys, and the order is the
 * Apps Script's.
 */
export const LEAVE_TAB_LABELS: Record<LeaveTab, string> = {
  calendar: 'Calendar',
  request: 'Request Leave',
  all: 'All Requests',
  approvals: 'Approvals',
};

/** The two an approver alone can open. */
const APPROVER_ONLY: readonly LeaveTab[] = ['all', 'approvals'];

export function isApproverOnly(tab: LeaveTab): boolean {
  return APPROVER_ONLY.includes(tab);
}

/** The tabs this person actually gets, in order. */
export function tabsFor(isApprover: boolean): LeaveTab[] {
  const keys = Object.keys(LEAVE_TAB_LABELS) as LeaveTab[];
  return isApprover ? keys : keys.filter((key) => !isApproverOnly(key));
}

/**
 * Which tab to open on.
 *
 * Calendar, because it answers the question the most people arrive with: who
 * is off. Filing a day is the second most common and sits one click away.
 *
 * The requested tab comes from the URL, so a bookmark or a link from Slack
 * lands where it says. An unknown name, or one this person may not open, falls
 * back rather than erroring — a link shared from an approver to a member
 * should show the member the page, not a wall.
 */
export function initialTab(requested: string | null, isApprover: boolean): LeaveTab {
  const allowed = tabsFor(isApprover);
  return requested !== null && (allowed as string[]).includes(requested)
    ? (requested as LeaveTab)
    : 'calendar';
}

/**
 * Keep the current tab honest when approver rights arrive or go.
 *
 * `useIsLeaveApprover` resolves after the first render, so the page draws once
 * before it knows. Without this, a member briefly on 'all' — from a URL, or
 * from a flag being turned off in another tab — would stay on a screen whose
 * query is about to be refused, and see an error instead of a page.
 */
export function settleTab(current: LeaveTab, isApprover: boolean): LeaveTab {
  return (tabsFor(isApprover) as string[]).includes(current) ? current : 'calendar';
}
